
// Типы запросов для смарт-распознавания
export type QueryType = 'id' | 'email' | 'phone' | 'text';

// Определение типа запроса на основе паттернов
export const detectQueryType = (query: string): QueryType => {
  const trimmedQuery = query.trim();
  
  // Email формат (проверяем первым, чтобы избежать ложных срабатываний)
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedQuery)) {
    return 'email';
  }
  
  // Телефон (только цифры, плюс, дефисы, скобки и пробелы)
  if (/^[+]?[\d\s\-()]{7,}$/.test(trimmedQuery)) {
    return 'phone';
  }
  
  // UUID формат (строго 8-4-4-4-12 символов или 32+ символа только hex)
  if (/^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i.test(trimmedQuery) || 
      /^[a-f0-9]{32,}$/i.test(trimmedQuery)) {
    return 'id';
  }
  
  // Короткие числовые ID (только цифры, 3+ символов)
  if (/^\d{3,}$/.test(trimmedQuery)) {
    return 'id';
  }
  
  // Короткие ID с буквами и цифрами (например, "b4", "abc123")
  if (/^[a-z0-9]{2,8}$/i.test(trimmedQuery) && /\d/.test(trimmedQuery)) {
    return 'id';
  }
  
  return 'text';
};

// Улучшенный расчет релевантности с учетом типа запроса
export const calculateRelevance = (
  query: string, 
  fields: (string | null)[], 
  queryType: QueryType = 'text'
): number => {
  const lowerQuery = query.toLowerCase();
  let relevance = 0;

  fields.forEach((field, index) => {
    if (!field) return;
    
    const lowerField = field.toLowerCase();
    
    // Бонус за соответствие типа запроса
    let typeBonus = 0;
    if (queryType === 'id' && (field.includes(query) || field.startsWith(query))) {
      typeBonus = 50; // Высокий приоритет для ID
    } else if (queryType === 'email' && field.includes('@')) {
      typeBonus = 40; // Высокий приоритет для email полей
    } else if (queryType === 'phone' && /[\d+\-()]/.test(field)) {
      typeBonus = 40; // Высокий приоритет для телефонных полей
    }
    
    // Точное совпадение - максимальный балл
    if (lowerField === lowerQuery) {
      relevance += 100 - index * 5 + typeBonus;
    }
    // Начинается с запроса - высокий балл
    else if (lowerField.startsWith(lowerQuery)) {
      relevance += 80 - index * 5 + typeBonus;
    }
    // Содержит запрос - средний балл
    else if (lowerField.includes(lowerQuery)) {
      relevance += 60 - index * 5 + typeBonus;
    }
    // Фонетическое сходство для текстовых запросов (упрощенная версия)
    else if (queryType === 'text' && calculatePhoneticSimilarity(lowerQuery, lowerField) > 0.7) {
      relevance += 40 - index * 5;
    }
  });

  return relevance;
};

// Упрощенная фонетическая схожесть для русских имен
const calculatePhoneticSimilarity = (query: string, field: string): number => {
  if (query.length < 3 || field.length < 3) return 0;
  
  // Простая метрика на основе общих подстрок
  const commonSubstrings = [];
  for (let i = 0; i < query.length - 1; i++) {
    const substring = query.substr(i, 2);
    if (field.includes(substring)) {
      commonSubstrings.push(substring);
    }
  }
  
  return commonSubstrings.length / Math.max(query.length - 1, field.length - 1);
};

export const getSearchHistory = (): string[] => {
  try {
    const history = localStorage.getItem('search_history');
    return history ? JSON.parse(history) : [];
  } catch {
    return [];
  }
};

export const addToSearchHistory = (query: string): void => {
  try {
    const history = getSearchHistory();
    const updated = [query, ...history.filter(q => q !== query)].slice(0, 10);
    localStorage.setItem('search_history', JSON.stringify(updated));
  } catch {
    // Ignore localStorage errors
  }
};
